/**
 * What each kind of account is allowed.
 *
 * These numbers used to be written into the server - five in a party for a
 * member, twenty for Premium, fifty for VIP - so deciding Premium was worth
 * more meant an edit and a deploy. One row per plan, one column per switch,
 * and every change takes effect on every signed-in client the moment it is
 * saved.
 *
 * A row is edited as a whole and saved as a whole: changing what Premium is
 * worth is a decision about Premium, not about six unrelated settings, and a
 * per-switch autosave meant half-finished states going live while somebody
 * was still thinking.
 */

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fileUrl } from '@/lib/api';
import { cn } from '@/lib/cn';
import { NumberField } from '@/components/NumberField';
import { Select } from '@/components/Select';
import { Button, Card, CardHead, Empty, Tag, Toggle, useConfirm, useToast } from '@/components/ui';
import { Icon } from '@/icons/Icon';
import type { CustomEmoji } from '@/lib/emoji';

type Field = {
  id: string;
  label: string;
  kind: 'int' | 'bool';
  min?: number;
  max?: number;
  unit?: string;
  note?: string;
};

type Values = Record<string, number | boolean>;

type PlanData = {
  fields: Field[];
  order: string[];
  labels: Record<string, string>;
  defaults: Record<string, Values>;
  plans: Record<string, Values>;
};

/** What each plan is, in one line, so the table is not read blind. */
const WHO: Record<string, string> = {
  none: 'Everybody who has not paid for anything.',
  premium: 'Premium members.',
  vip: 'VIP members.',
  mod: 'Moderators, whatever their membership.',
  admin: 'Administrators. Nothing here is ever refused to them.'
};

export function Permissions({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  const { data, isPending } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<PlanData>('/api/plans')
  });

  const [plan, setPlan] = useState('none');
  const [busy, setBusy] = useState(false);

  /*
   * What has been changed and not yet saved, per plan.
   *
   * Held as an overlay on the saved row rather than as a copy of it. A copy
   * had to be reset whenever the query refetched, and the query refetches on
   * window focus - so clicking away and back threw away whatever was being
   * typed, silently, with the screen still showing the new number and the
   * Save button greyed out because nothing had officially changed.
   */
  const [edits, setEdits] = useState<Record<string, Values>>({});

  const saved = data?.plans[plan];
  const mine = edits[plan] ?? {};
  const draft: Values | null = saved ? { ...saved, ...mine } : null;

  const dirty = useMemo(
    () => !!saved && Object.keys(mine).some((k) => mine[k] !== saved[k]),
    [mine, saved]
  );

  const change = (id: string, value: number | boolean) =>
    setEdits((prev) => ({ ...prev, [plan]: { ...(prev[plan] ?? {}), [id]: value } }));

  const undo = () => setEdits((prev) => { const next = { ...prev }; delete next[plan]; return next; });

  async function save() {
    if (!draft) return;
    setBusy(true);
    try {
      await api.patch('/api/admin/plans', { plan, values: draft });
      undo();
      toast((data?.labels[plan] ?? plan) + ' updated', 'good');
      await queryClient.invalidateQueries({ queryKey: ['plans'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    const ok = await confirm(
      'Put every plan back?',
      'All five rows go back to the numbers the app shipped with.',
      { confirmLabel: 'Reset all', danger: true }
    );
    if (!ok) return;
    try {
      await api.post('/api/admin/plans/reset', {});
      setEdits({});
      toast('Back to the defaults', 'good');
      await queryClient.invalidateQueries({ queryKey: ['plans'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  if (isPending || !data || !draft) {
    return <Card><div className="h-[280px] animate-pulse rounded-[var(--radius)] bg-surface-1" /></Card>;
  }

  const numbers = data.fields.filter((f) => f.kind === 'int');
  const switches = data.fields.filter((f) => f.kind === 'bool');
  const base = data.defaults[plan] ?? {};

  return (
    <div className="flex flex-col gap-[var(--gap)]">
      <Card>
        <CardHead
          title="What each plan gets"
          note={isAdmin
            ? 'Changes reach everyone the moment you save them.'
            : 'Read-only — only an administrator can change these.'}
          actions={isAdmin ? <Button kind="outline" size="sm" onClick={() => void reset()}>Reset all</Button> : null}
        />

        {/* Which plan. Kept as a row of pills rather than five separate cards:
            the point of this screen is comparing one against the next. */}
        <div className="flex flex-wrap gap-1.5">
          {data.order.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setPlan(id)}
              aria-pressed={plan === id}
              className={cn(
                'h-[32px] rounded-[calc(10px*var(--radius-mul,1))] border px-3.5 text-[12.5px] font-bold transition-colors',
                plan === id
                  ? 'border-accent-line bg-accent-soft text-accent-hi'
                  : 'border-line-soft bg-surface-1 text-mute hover:border-line hover:text-dim'
              )}
            >
              {data.labels[id] ?? id}
            </button>
          ))}
        </div>

        <p className="mt-2 text-[12px] text-mute">{WHO[plan]}</p>

        {/* the numbers */}
        <div className="mt-[var(--gap)] grid gap-[var(--gap)] sm:grid-cols-3">
          {numbers.map((f) => (
            <div key={f.id}>
              <span className="mb-2 flex items-center gap-2">
                <span id={'perm-' + f.id} className="text-[14px] font-semibold text-ink">{f.label}</span>
                {draft[f.id] !== base[f.id] ? <Tag kind="accent">Changed</Tag> : null}
              </span>
              <NumberField
                value={Number(draft[f.id])}
                min={f.min ?? 0}
                max={f.max ?? 9999}
                disabled={!isAdmin}
                suffix={f.unit}
                label={f.label}
                onChange={(n) => change(f.id, n)}
              />
              {f.note ? <p className="mt-1.5 text-[13px] text-mute">{f.note}</p> : null}
            </div>
          ))}
        </div>

        {/* the switches */}
        <div className="mt-[var(--gap)] flex flex-col divide-y divide-line-soft border-t border-line-soft">
          {switches.map((f) => (
            <Toggle
              key={f.id}
              checked={!!draft[f.id]}
              disabled={!isAdmin}
              onChange={(on) => change(f.id, on)}
              label={f.label}
              note={f.note}
            />
          ))}
        </div>

        {isAdmin ? (
          <div className="mt-[var(--gap)] flex items-center gap-2 border-t border-line-soft pt-[var(--gap)]">
            <span className="flex-1 text-[12px] text-mute">
              {dirty ? 'Unsaved changes to ' + (data.labels[plan] ?? plan) + '.' : 'Nothing to save.'}
            </span>
            <Button kind="ghost" disabled={!dirty} onClick={undo}>Undo</Button>
            <Button kind="primary" disabled={!dirty || busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save ' + (data.labels[plan] ?? plan)}
            </Button>
          </div>
        ) : null}
      </Card>

      <EmojiAccess />
      {dialog}
    </div>
  );
}

/**
 * Who may use each custom emoji.
 *
 * Separate from the plan table because it is per-emoji rather than per-plan:
 * one reserved for VIP is a thing about that emoji, not a switch on VIP.
 */
function EmojiAccess() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isPending } = useQuery({
    queryKey: ['emoji', 'admin'],
    queryFn: () => api.get<{ emoji: CustomEmoji[] }>('/api/emoji')
  });

  const options = [
    { value: 'everyone', label: 'Everyone', icon: 'globe' },
    { value: 'premium', label: 'Premium and above', icon: 'badgePremium', color: 'var(--tier-epic)' },
    { value: 'vip', label: 'VIP only', icon: 'badgeVip', color: 'var(--tier-mythic)' },
    { value: 'staff', label: 'Staff only', icon: 'shieldNav' }
  ];

  async function setAccess(id: string, access: string) {
    try {
      await api.patch('/api/admin/emoji/' + id, { access });
      await queryClient.invalidateQueries({ queryKey: ['emoji'] });
      toast('Access updated', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  const list = data?.emoji ?? [];

  return (
    <Card>
      <CardHead
        title="Who can use which emoji"
        note="Reserving one keeps it out of the picker, and out of a status somebody typed by hand."
        actions={<Tag>{list.length}</Tag>}
      />

      {isPending ? (
        <div className="h-[120px] animate-pulse rounded-[var(--radius)] bg-surface-1" />
      ) : !list.length ? (
        <Empty title="No custom emoji yet" note="Upload some on the Emoji tab first." />
      ) : (
        <div className="flex flex-col gap-1">
          {list.map((e) => (
            <div key={e.id} className="flex items-center gap-3 rounded-[calc(10px*var(--radius-mul,1))] px-2 py-1.5 transition-colors hover:bg-surface-1">
              <img src={fileUrl(e.url)} alt="" className="h-[26px] w-[26px] flex-none object-contain" />
              <span className="min-w-0 flex-1 truncate font-mono text-[12.5px] text-dim">:{e.name}:</span>
              {e.access && e.access !== 'everyone'
                ? <Icon name="lock" size={12} className="flex-none text-ghost" />
                : null}
              <Select
                search={false}
                size="sm"
                className="w-[190px] flex-none"
                value={e.access ?? 'everyone'}
                onChange={(next) => void setAccess(e.id, next)}
                options={options}
              />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
